import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../context/authContext";
import { useDropzone } from "react-dropzone";
import { baseAxios } from "../../apis";
import { useCsrfToken } from "../../hooks/useCsrfToken";
import Avatar from "../commons/Avatar";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import Dialog from "../dialogs";
import { ArrowUpTrayIcon } from "@heroicons/react/16/solid";
import DialogCropper from "../dialogs/DialogCropper";
import imageToFile from "../../utils";
import DialogSuccess from "../dialogs/DialogSuccess";
import LogoutButton from "../logout/LogoutButton";
import useAuthedApi from "../../hooks/useAuthedApi";
import { useToast } from "../../context/toastContext";
import DialogAsk from "../dialogs/DialogAsk";
import { useNavigate } from "react-router-dom";

export default function AccountDetails() {
  const { addToast } = useToast();
  const { user, fetchUser, logout } = useAuth();
  const { authedPatch, authedPost } = useAuthedApi();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [originalForm, setOriginalForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
  });

  const [profilePicture, setProfilePicture] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [openDialog, setOpenDialog] = useState(null);

  const [isEditBasicInformation, setIsEditBasicInformation] = useState(false);
  const [isEditPassword, setIsEditPassword] = useState(false);

  const [croppingImage, setCroppingImage] = useState(null);
  const [showCropper, setShowCropper] = useState(false);

  // Deactivate account state
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [deactivateReason, setDeactivateReason] = useState("");
  const [deactivateOther, setDeactivateOther] = useState("");

  const DEACTIVATE_REASONS = [
    "I am reducing the number of accounts or apps I use",
    "I signed up by mistake",
    "I have concerns about account management or control",
    "I am taking a break from trading or investing",
    "The service is not relevant to me at this time",
    "I am concerned about fees or costs",
    "I did not understand how to use the platform",
    "I did not complete my account setup",
    "I created this account only to explore the platform",
    "Other",
  ];

  useEffect(() => {
    if (user) {
      const userData = {
        first_name: user?.first_name || "",
        last_name: user?.last_name || "",
        email: user?.email || "",
      };
      setForm({ ...userData, password: "", confirmPassword: "" });
      setOriginalForm(userData);
    }
  }, [user]);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles?.[0];
    if (file) {
      const preview = URL.createObjectURL(file);
      setCroppingImage(preview);
      setShowCropper(true);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [".jpeg", ".png", ".jpg", ".gif"] },
    multiple: false,
  });

  const handleCropComplete = async (croppedBlob) => {
    const file = await imageToFile(croppedBlob, "profile_image.jpg");
    setProfilePicture(file);
    await handleUploadProfileImage(file);
  };

  const handleUploadProfileImage = async (file) => {
    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append("profile_image", file);
      await authedPost("/api/user/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreviewUrl(URL.createObjectURL(file));
      fetchUser(false);
      setOpenDialog("success-update-profile");
      setShowCropper(false);
    } catch (error) {
      console.error("UPLOAD FAILED", error);
      addToast("error", "Failed to upload image");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelBasicInfo = () => {
    setForm((prev) => ({ ...prev, ...originalForm }));
    setIsEditBasicInformation(false);
  };

  const handleCancelPassword = () => {
    setForm((prev) => ({ ...prev, password: "", confirmPassword: "" }));
    setIsEditPassword(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitMessage("");
    if (isSubmitting) return;
    setIsSubmitting(true);

    const formData = new FormData();
    if (profilePicture) formData.append("profile_image", profilePicture);

    if (isEditPassword) {
      if (form.password && form.password === form.confirmPassword) {
        formData.append("password", form.password);
        formData.append("confirm_password", form.confirmPassword);
      } else if (form.password !== form.confirmPassword) {
        setSubmitMessage("Passwords do not match.");
        setIsSubmitting(false);
        return;
      }
    }

    if (isEditBasicInformation) {
      formData.append("first_name", form.first_name);
      formData.append("last_name", form.last_name);
      if (form.email !== user?.email) formData.append("email", form.email);
    }

    try {
      await authedPatch("/api/edit/user-detail", formData);
      setOpenDialog("success-update-profile");
      setIsEditPassword(false);
      setIsEditBasicInformation(false);

      if (isEditBasicInformation) {
        setOriginalForm({
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
        });
      }
      if (isEditPassword) {
        setForm((prev) => ({ ...prev, password: "", confirmPassword: "" }));
      }
    } catch (error) {
      console.error("Failed to update profile:", error.response?.data || error);
      setSubmitMessage("Failed to update profile. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Deactivate account ──────────────────────────────────────────────────────
  const handleDeactivateConfirm = async () => {
    setIsDeactivating(true);
    const reason = deactivateReason === "Other" ? deactivateOther : deactivateReason;
    try {
      await authedPost("/api/deactivate-account", { reason });
      setOpenDialog(null);
      if (logout) await logout();
      // Toast won't survive a full logout/remount, so carry it via location state
      // and let LoginForm read + display it on arrival
      localStorage.setItem(
        "pending_toast",
        JSON.stringify({
          type: "success",
          message:
            "Your account has been deactivated. You can reactivate it anytime by signing up again with the same email.",
        }),
      );
      navigate("/login", { replace: true });
    } catch (error) {
      console.error(
        "Failed to deactivate account:",
        error.response?.data || error,
      );
      addToast("error", "Failed to deactivate account. Please try again.");
      setOpenDialog(null);
    } finally {
      setIsDeactivating(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row gap-4 mb-5 md:mb-0">
      <div className="w-full bg-white">
        {/* ── Dialogs ── */}
        <DialogSuccess
          open={openDialog === "success-update-profile"}
          onClose={() => setOpenDialog(null)}
          title="Profile updated successfully!"
        />

        <DialogAsk
          open={openDialog === "deactivate-account"}
          onClose={() => {
            if (isDeactivating) return;
            setOpenDialog(null);
            setDeactivateReason("");
            setDeactivateOther("");
          }}
          title="Are you sure you want to deactivate your account? You can reactivate it later by re-registering with the same email."
          onConfirm={handleDeactivateConfirm}
          loading={isDeactivating}
          width="520px"
          confirmDisabled={!deactivateReason || (deactivateReason === "Other" && !deactivateOther.trim())}
        >
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Please select a reason:</p>
            {DEACTIVATE_REASONS.map((reason) => (
              <div key={reason}>
                <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="deactivate-reason"
                    value={reason}
                    checked={deactivateReason === reason}
                    onChange={() => {
                      setDeactivateReason(reason);
                      if (reason !== "Other") setDeactivateOther("");
                    }}
                    className="w-4 h-4 accent-[#007A78] shrink-0"
                    disabled={isDeactivating}
                  />
                  <span className="text-sm text-gray-700">
                    {reason === "Other" ? "Other, state the reason" : reason}
                  </span>
                </label>
                {reason === "Other" && deactivateReason === "Other" && (
                  <textarea
                    className="w-full mt-1 ml-9 !w-[calc(100%-2.25rem)] p-2 text-sm border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-[#007A78]"
                    rows={3}
                    placeholder="Please state your reason..."
                    value={deactivateOther}
                    onChange={(e) => setDeactivateOther(e.target.value)}
                    disabled={isDeactivating}
                    autoFocus
                  />
                )}
              </div>
            ))}
          </div>
        </DialogAsk>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-7 gap-5">
            {/* ── Profile Picture ── */}
            <div className="lg:col-span-1 flex flex-col lg:items-center rounded-xl">
              <div
                className="relative cursor-pointer w-fit"
                onClick={() => setOpenDialog("edit-image")}
              >
                <Avatar />
                <div className="absolute bottom-[-.15rem] right-[-.15rem] p-2 bg-white rounded-full border border-[#007A78] w-fit">
                  <PencilSquareIcon className="w-[20px] h-[20px] text-[#007A78]" />
                </div>
              </div>

              <DialogCropper
                open={showCropper && croppingImage}
                image={croppingImage}
                onClose={() => setShowCropper(false)}
                onComplete={handleCropComplete}
                isLoading={isSubmitting}
              />

              <Dialog
                open={openDialog === "edit-image"}
                onClose={() => setOpenDialog(null)}
                width="500px"
              >
                <div>
                  <h2 className="text-xl font-bold text-[#21272A] flex items-center capitalize">
                    Edit Photo
                  </h2>
                </div>
                <div className="mt-4">
                  <input {...getInputProps()} id="profile-picture-upload" />
                  <div
                    className="flex items-center py-3 gap-4 cursor-pointer"
                    {...getRootProps()}
                  >
                    <ArrowUpTrayIcon className="w-[24px] h-[24px] text-[#007A78]" />
                    Upload Photo
                  </div>
                </div>
              </Dialog>
            </div>

            {/* ── Account Details Form ── */}
            <div className="lg:col-span-6 space-y-4">
              {/* Basic Information */}
              <div className="space-y-4">
                <div className="flex items-end justify-between mb-3">
                  <h2 className="text-2xl font-bold text-gray-800">
                    Basic Information
                  </h2>
                  <button
                    className="!p-0 !bg-transparent text-[#007A78]"
                    onClick={() => {
                      if (isSubmitting) return;
                      if (isEditPassword) handleCancelPassword();
                      if (isEditBasicInformation) {
                        handleCancelBasicInfo();
                      } else {
                        setIsEditBasicInformation(true);
                      }
                    }}
                    type="button"
                  >
                    {isEditBasicInformation ? "Cancel" : "Edit"}
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="first_name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      First Name
                    </label>
                    <input
                      type="text"
                      id="first_name"
                      placeholder="John"
                      className="mt-0 read-only:text-[#B7BABD] read-only:focus:outline-none"
                      value={form.first_name}
                      onChange={(e) =>
                        setForm({ ...form, first_name: e.target.value })
                      }
                      readOnly={!isEditBasicInformation}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="last_name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="last_name"
                      className="mt-0 read-only:text-[#B7BABD] read-only:focus:outline-none"
                      placeholder="Doe"
                      value={form.last_name}
                      onChange={(e) =>
                        setForm({ ...form, last_name: e.target.value })
                      }
                      readOnly={!isEditBasicInformation}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Email (login)
                  </label>
                  <input
                    type="email"
                    id="email"
                    className="mt-0 read-only:text-[#B7BABD] read-only:focus:outline-none"
                    placeholder="johndoe@gmail.com"
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                    readOnly
                  />
                </div>

                {isEditBasicInformation && (
                  <div className="flex justify-start items-center">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-fit relative overflow-hidden !py-3 !font-bold !text-base !bg-[#007A78]"
                    >
                      {isSubmitting ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                )}
              </div>

              {/* Change Password */}
              <div>
                <div className="flex items-end justify-between mb-3">
                  <h2 className="text-2xl font-bold text-gray-800">
                    Change Password
                  </h2>
                  <button
                    className="!p-0 !bg-transparent text-[#007A78]"
                    onClick={() => {
                      if (isSubmitting) return;
                      if (isEditBasicInformation) handleCancelBasicInfo();
                      if (isEditPassword) {
                        handleCancelPassword();
                      } else {
                        setIsEditPassword(true);
                      }
                    }}
                    type="button"
                  >
                    {isEditPassword ? "Cancel" : "Edit"}
                  </button>
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {isEditPassword && "New "}Password
                  </label>
                  <div className="relative">
                    <input
                      type="password"
                      id="password"
                      className="mt-0 read-only:text-[#B7BABD] read-only:focus:outline-none"
                      placeholder="Enter new password"
                      value={
                        !isEditPassword
                          ? "ASDjkasdgjasgdagsdjhagsjdgasjdgashgdhasgdjasgdjasgdjgasdgasgdjasgdqywieyqwe8"
                          : form.password
                      }
                      onChange={(e) =>
                        setForm({ ...form, password: e.target.value })
                      }
                      readOnly={!isEditPassword}
                    />
                  </div>
                </div>

                {isEditPassword && (
                  <div className="mt-4">
                    <label
                      htmlFor="confirmPassword"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <input
                        type="password"
                        id="confirmPassword"
                        className="mt-0 read-only:text-[#B7BABD] read-only:focus:outline-none"
                        placeholder="Confirm new password"
                        value={form.confirmPassword}
                        onChange={(e) =>
                          setForm({ ...form, confirmPassword: e.target.value })
                        }
                        readOnly={!isEditPassword}
                      />
                    </div>
                  </div>
                )}

                {!!submitMessage && (
                  <span className="text-[#ff0000] text-sm mt-3 block">
                    {submitMessage}
                  </span>
                )}

                {isEditPassword && (
                  <div className="flex justify-start items-center pt-2">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-fit relative overflow-hidden !py-3 !font-bold !text-base !bg-[#007A78]"
                    >
                      {isSubmitting ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                )}

                {/* ── Footer actions: Logout + Deactivate ── */}
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setOpenDialog("deactivate-account")}
                    disabled={isSubmitting || isDeactivating}
                    className="!p-0 !bg-transparent text-red-500 hover:text-red-700 text-sm font-medium flex items-center gap-1.5 transition-colors"
                  >
                    <TrashIcon className="w-4 h-4" />
                    Deactivate Account
                  </button>

                  <LogoutButton />
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
